/**
 * Active Carts Page
 * 
 * Displays all active customer carts with filtering, sorting, and pagination.
 * Allows sales team to view cart details and take quick actions.
 */
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Grid
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridSortModel
} from '@mui/x-data-grid';
import {
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
  History as HistoryIcon,
  Phone as PhoneIcon,
  Warning as WarningIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CartService } from '../services/cartService';
import { ActiveCart, CartFilters } from '../types/activeCart';
import CartDetailModal from '../components/carts/CartDetailModal';
import { formatCurrency } from '../utils/currencyFormatters';

const ActiveCartsPage: React.FC = () => {
  const navigate = useNavigate();
  
  // Filter state
  const [filters, setFilters] = useState<CartFilters>({
    page: 1,
    page_size: 20,
    sort_by: 'value',
    sort_order: 'desc'
  });

  // Modal state
  const [selectedCartId, setSelectedCartId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Fetch active carts
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['active-carts', filters],
    queryFn: () => CartService.getActiveCarts(filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Handle filter changes
  const handleFilterChange = (field: keyof CartFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: field !== 'page' ? 1 : value // Reset to page 1 when filters change
    }));
  };

  // Handle sort changes
  const handleSortModelChange = (model: GridSortModel) => {
    if (model.length > 0) {
      const { field, sort } = model[0];
      setFilters(prev => ({
        ...prev,
        sort_by: field as 'value' | 'age' | 'last_activity',
        sort_order: sort as 'asc' | 'desc'
      }));
    }
  };

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    handleFilterChange('page', newPage + 1); // MUI DataGrid uses 0-based indexing
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setFilters(prev => ({
      ...prev,
      page_size: newPageSize,
      page: 1
    }));
  };

  // Handle cart detail view
  const handleViewCart = (cartId: string) => {
    setSelectedCartId(cartId);
    setModalOpen(true);
  };

  // Handle quick actions
  const handleContactCustomer = (phone: string) => {
    if (phone) {
      window.location.href = `tel:${phone}`;
    }
  };

  const handleViewCustomerProfile = (customerId: string) => {
    navigate(`/admin/customers/${customerId}`);
  };

  const handleViewOrderHistory = (customerId: string) => {
    navigate(`/admin/customers/${customerId}?tab=orders`);
  };

  const handleExportActiveCarts = async () => {
    try {
      const blob = await CartService.exportActiveCarts(filters);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `active_carts_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting active carts:', error);
      // You could add a toast notification here
    }
  };

  // Calculate cart age in days for display
  const getCartAgeDays = (ageHours: number): number => {
    return Math.floor(ageHours / 24);
  };

  // Check if cart is stale (>7 days)
  const isStaleCart = (ageHours: number): boolean => {
    return ageHours > 168; // 7 days = 168 hours
  };

  // Format last activity timestamp
  const formatLastActivity = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffHours < 1) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return `${diffMinutes} min ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hours ago`;
    } else {
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays} days ago`;
    }
  };

  // Define columns
  const columns: GridColDef[] = [
    {
      field: 'customer_name',
      headerName: 'Customer',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<ActiveCart>) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {params.row.customer_name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.customer_type}
          </Typography>
        </Box>
      )
    },
    {
      field: 'item_count',
      headerName: 'Items',
      width: 100,
      align: 'center',
      headerAlign: 'center'
    },
    {
      field: 'total_value',
      headerName: 'Cart Value',
      width: 150,
      renderCell: (params: GridRenderCellParams<ActiveCart>) => (
        <Typography variant="body2" fontWeight="medium">
          {formatCurrency(params.row.total_value)}
        </Typography>
      )
    },
    {
      field: 'cart_age_hours',
      headerName: 'Age',
      width: 120,
      renderCell: (params: GridRenderCellParams<ActiveCart>) => {
        const ageDays = getCartAgeDays(params.row.cart_age_hours);
        const isStale = isStaleCart(params.row.cart_age_hours);
        
        return (
          <Box display="flex" alignItems="center" gap={1}>
            {isStale && (
              <Tooltip title="Cart inactive for more than 7 days">
                <WarningIcon color="warning" fontSize="small" />
              </Tooltip>
            )}
            <Typography
              variant="body2"
              color={isStale ? 'warning.main' : 'text.primary'}
              fontWeight={isStale ? 'medium' : 'normal'}
            >
              {ageDays} days
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'last_activity',
      headerName: 'Last Activity',
      width: 150,
      renderCell: (params: GridRenderCellParams<ActiveCart>) => (
        <Typography variant="body2" color="text.secondary">
          {formatLastActivity(params.row.last_activity)}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      renderCell: (params: GridRenderCellParams<ActiveCart>) => (
        <Box display="flex" gap={0.5}>
          <Tooltip title="View Cart Details">
            <IconButton
              size="small"
              onClick={() => handleViewCart(params.row.cart_id)}
              color="primary"
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Contact Customer">
            <IconButton
              size="small"
              onClick={() => handleContactCustomer(params.row.customer_phone || '')}
              disabled={!params.row.customer_phone}
              color="primary"
            >
              <PhoneIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="View Customer Profile">
            <IconButton
              size="small"
              onClick={() => handleViewCustomerProfile(params.row.customer_id)}
              color="primary"
            >
              <PersonIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="View Order History">
            <IconButton
              size="small"
              onClick={() => handleViewOrderHistory(params.row.customer_id)}
              color="primary"
            >
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Active Carts
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Monitor customer carts and identify sales opportunities
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Tooltip title="Export active carts to CSV">
            <Button
              variant="contained"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={handleExportActiveCarts}
              disabled={isLoading}
            >
              Export CSV
            </Button>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Summary Stats */}
      {data && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Total Active Carts
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {data.total}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Total Cart Value
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {formatCurrency(data.total_value || 0)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Average Cart Value
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {formatCurrency(data.average_value || 0)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Stale Carts (&gt;7 days)
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="warning.main">
                {data.stale_carts_count || 0}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Customer Type"
              value={filters.customer_type || ''}
              onChange={(e) => handleFilterChange('customer_type', e.target.value || undefined)}
              size="small"
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="retail">Retail</MenuItem>
              <MenuItem value="reseller">Reseller</MenuItem>
              <MenuItem value="wholesaler">Wholesaler</MenuItem>
              <MenuItem value="broker">Broker</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              type="number"
              label="Min Value"
              value={filters.min_value || ''}
              onChange={(e) => handleFilterChange('min_value', e.target.value ? parseFloat(e.target.value) : undefined)}
              size="small"
              inputProps={{ min: 0, step: 100 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              type="number"
              label="Max Value"
              value={filters.max_value || ''}
              onChange={(e) => handleFilterChange('max_value', e.target.value ? parseFloat(e.target.value) : undefined)}
              size="small"
              inputProps={{ min: 0, step: 100 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              type="number"
              label="Min Age (hours)"
              value={filters.min_age_hours || ''}
              onChange={(e) => handleFilterChange('min_age_hours', e.target.value ? parseInt(e.target.value) : undefined)}
              size="small"
              inputProps={{ min: 0, step: 24 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              type="number"
              label="Max Age (hours)"
              value={filters.max_age_hours || ''}
              onChange={(e) => handleFilterChange('max_age_hours', e.target.value ? parseInt(e.target.value) : undefined)}
              size="small"
              inputProps={{ min: 0, step: 24 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setFilters({ page: 1, page_size: 20, sort_by: 'value', sort_order: 'desc' })}
              size="small"
              sx={{ height: '40px' }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load active carts. Please try again.
        </Alert>
      )}

      {/* Data Grid */}
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={data?.carts || []}
          columns={columns}
          getRowId={(row) => row.cart_id}
          loading={isLoading}
          paginationMode="server"
          rowCount={data?.total || 0}
          paginationModel={{
            page: (filters.page || 1) - 1,
            pageSize: filters.page_size || 20
          }}
          onPaginationModelChange={(model) => {
            setFilters(prev => ({
              ...prev,
              page: model.page + 1,
              page_size: model.pageSize
            }));
          }}
          pageSizeOptions={[10, 20, 50, 100]}
          sortingMode="server"
          sortModel={[
            {
              field: filters.sort_by || 'value',
              sort: filters.sort_order || 'desc'
            }
          ]}
          onSortModelChange={handleSortModelChange}
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-row:hover': {
              cursor: 'pointer'
            }
          }}
          onRowClick={(params) => handleViewCart(params.row.cart_id)}
        />
      </Paper>

      {/* Cart Detail Modal */}
      {selectedCartId && (
        <CartDetailModal
          cartId={selectedCartId}
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedCartId(null);
          }}
        />
      )}
    </Box>
  );
};

export default ActiveCartsPage;
